import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { gameTypeToUrlPrefix } from '@loyalty/shared-common/premium-games-api-client';
import { renderToString } from 'react-dom/server';
import { resolveAdvancedFormatConfigId, uuid } from '@loyalty/shared-common';
import {
  AdvancedFormatBackendProps,
  AdvancedFormatComponentAttributes,
  AdvancedFormatComponentI18nAttributes,
  AdvancedFormatOpenType,
} from '../../../grapes-components-types/advanced-format-types';
import { AdvancedFormatNewTabCard } from './AdvancedFormatNewTabCard';
import { GenericComponent } from '../../components-tree';
import { ReactBootstrap } from '../../ReactBootstrap';

export { AdvancedFormatOpenType };
export type { AdvancedFormatComponentAttributes, AdvancedFormatBackendProps };

export type AdvancedFormatComponent = GenericComponent<
  AdvancedFormatComponentAttributes,
  AdvancedFormatComponentI18nAttributes
>;

interface AdvancedFormatComponentFrontendProps {
  id: string;
  locale: string;
  programId: string;
  templateId: string;
  isInternalUser: boolean;
  attributes: AdvancedFormatComponent['attributes'];
  i18nAttributes?: AdvancedFormatComponent['i18nAttributes'];
  isEditorPreview: boolean;
}

function serializeAdvancedFormatComponentProps(
  props: AdvancedFormatBackendProps,
  isEditorPreview: boolean,
): AdvancedFormatComponentFrontendProps {
  const { state, options, context } = props;
  const { programId, templateId, isInternalUser } = state;
  const { locale } = context.i18n;

  return {
    id: options.id || options.attributes.id || uuid(),
    locale,
    programId,
    attributes: options.attributes,
    i18nAttributes: options.i18nAttributes,
    isEditorPreview,
    templateId,
    isInternalUser,
  };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
    },
  },
});

function Preview({ message }: { message?: string }) {
  return (
    <div
      style={{
        minHeight: 320,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        border: '1px dashed #c1c7d0',
        background: '#f8f9fb',
        color: '#495057',
        textAlign: 'center',
      }}
    >
      {message}
    </div>
  );
}

function ErrorState({ error, retry }: { error: Error; retry: () => void }) {
  return (
    <div style={{ padding: 16, textAlign: 'center' }}>
      <p>{error.message}</p>
      <button onClick={retry}>Retry</button>
    </div>
  );
}

function InnerAdvancedFormatComponent({
  programId,
  templateId,
  locale,
  attributes,
  i18nAttributes,
  isEditorPreview,
  isInternalUser,
}: AdvancedFormatComponentFrontendProps) {
  const openType = attributes.openType ?? AdvancedFormatOpenType.iframe;
  const configUrl = attributes.configUrl;
  const configId = resolveAdvancedFormatConfigId(attributes);
  const gameType = attributes.gameType;

  if (!gameType) {
    return <Preview message="Advanced format game is required" />;
  }

  const urlPrefix = gameTypeToUrlPrefix[gameType];
  if (!urlPrefix) {
    console.error('Invalid game type', gameType);
    return <Preview message="Invalid game type" />;
  }

  if (!configId) {
    return <Preview message="Game is required" />;
  }

  if (openType === AdvancedFormatOpenType.newTab) {
    return (
      <AdvancedFormatNewTabCard
        isInternalUser={isInternalUser}
        programId={programId}
        templateId={templateId}
        locale={locale}
        attributes={attributes}
        i18nAttributes={i18nAttributes}
        isEditorPreview={isEditorPreview}
      />
    );
  }

  const params = new URLSearchParams({
    program_id: programId,
    lang: locale,
    config_id: configId,
    context_id: templateId,
  });
  if (isInternalUser) {
    params.set('internalUser', 'true'); // will be used to avoid cache
  }
  if (isEditorPreview) {
    params.set('preview', 'true');
  }

  return (
    <iframe
      id="advanced-format-game-frame"
      title="Advanced format game"
      src={`/advanced_formats/${urlPrefix}/?${params}`}
      style={{ width: '100%', minHeight: 480, border: 0 }}
    />
  );
}

export function AdvancedFormatComponent(props: AdvancedFormatComponentFrontendProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <InnerAdvancedFormatComponent {...props} />
    </QueryClientProvider>
  );
}

export function AdvancedFormatRenderer(
  props: AdvancedFormatBackendProps,
  document?: Document,
): string {
  const reactProps = serializeAdvancedFormatComponentProps(props, !!document);
  return renderToString(
    <ReactBootstrap
      componentType="advanced-format"
      component="AdvancedFormatComponent"
      props={reactProps}
      document={document}
    />,
  );
}
